import type {
  AllVehicleExpirationTypesShallowQuery,
  VehicleDetailNode,
  VehicleExpirationTypeOrdering,
  VehicleOrdering,
} from './types.graphql'
import type { RelayNodeType } from '../../types/graphql'

/* Forms */
export interface EditVehicleForm {
  id: string
}

export interface VehicleTableFilter {
  query: string
  orderBy: string
  owned: boolean
}

export interface VehicleFilter {
  query?: string
  owned: boolean
  orderBy: VehicleOrdering
  includedTypes: string[]
}

export interface Filter {
  q?: string
  owned?: boolean
  orderBy: string
  filledBy?: string
  excludeTypes?: string[]
}

export interface VehicleExpirationFilterType {
  expirationTypes?: string[]
  orderBy?: VehicleExpirationTypeOrdering
}

export type VehicleExpirationTypeShallow = RelayNodeType<
  AllVehicleExpirationTypesShallowQuery['allVehicleExpirationTypes']
>

/* Forms */
export interface CreateVehicleForm {
  vin: string
}

export interface CreateVehicleModalResolve {
  form: CreateVehicleForm
}

export interface CreateVehicleByRegNrForm {
  registrationNumber: string
}

export interface AddVehicleForm {
  isDummy: boolean
  registrationNumber: string
  name: string
}

export interface AddVehicleModalResolve {
  form: AddVehicleForm
}

export interface EditVehicleDocumentForm {
  name: string
  description: string
  expirationDate: Date | null
  notificationTime: number | null
  file: File | string | null
  responsibleUser: string | null
  /**
   * Only asked for when the row is created away from a vehicle, i.e. from the
   * fleet-wide page. The per-vehicle tab supplies it itself.
   */
  vehicle: string | null
}

export interface EditVehicleDocumentPromptResolve {
  form: EditVehicleDocumentForm
  hasNotification: boolean
}

export interface EditVehicleExpirationForm {
  description: string
  expirationDate: Date
  notificationTime: number
  expirationType: string | null
  responsibleUser: string | null
  /**
   * Only asked for when the row is created away from a vehicle, i.e. from the
   * fleet-wide page. The per-vehicle tab supplies it itself.
   */
  vehicle: string | null
}
export interface EditVehicleExpirationPromptResolve {
  form: EditVehicleExpirationForm
  hasNotification: boolean
}

export type VehicleServicePlanStatus =
  | 'PLANNED'
  | 'BOOKED'
  | 'PERFORMED'
  | 'CANCELLED'

export const vehicleServicePlanStatuses = [
  'PLANNED',
  'BOOKED',
  'PERFORMED',
  'CANCELLED',
] as const satisfies readonly VehicleServicePlanStatus[]

/** Who wrote the row: a user, or an OEM integration that rewrites it on sync. */
export type VehicleServicePlanSource = 'MANUAL' | 'SCANIA'

export interface EditVehicleServicePlanForm {
  name: string
  description: string
  scheduledStart: Date
  scheduledEnd: Date
  status: VehicleServicePlanStatus
  workshop: string | null
  serviceTypes: string[]
  cost: number | null
  /** Actual work time, in minutes. Entered as hours plus minutes. */
  timeSpent: number | null
  notificationTime: number
  responsibleUser: string | null
  /**
   * Only asked for when the service is created away from a vehicle, i.e. from
   * the fleet-wide services page. The per-vehicle tab supplies it itself.
   */
  vehicle: string | null
}

export interface EditVehicleServicePlanPromptResolve {
  form: EditVehicleServicePlanForm
  hasNotification: boolean
}

export interface ExpirationTypes {
  [key: string]: string
  adr: string
  nextAdrControl: string
  nextTailLiftControl: string
  nextEuControl: string
  nextFireExtinguisherControl: string
  nextCraneControl: string
  nextPrinterCalibration: string
  expirationLeasing: string
  expirationServiceAgreement: string
}

/* Vehicle view */
export interface VehicleDetailInput {
  key: keyof EditVehicleDetailsForm | string
  unit?: string
  label?: string
  type?: string
  readOnly?: boolean
  options?: {
    label: string
    value: any
  }[]
  dependencies?: {
    requireFields?: string[]
  }
}

export type VehicleDetailInputDependency = 'require'

export interface DieselCardForm {
  id?: string | null
  number: string
  pinCode: string
}

type Nullable<T> = { [P in keyof T]?: T[P] | null }
export interface EditVehicleDetailsForm
  extends Nullable<
    Omit<
      VehicleDetailNode,
      | 'registrationType'
      | 'department'
      | 'registrationDate'
      | 'soldDate'
      | 'nextPeriodicControl'
      | 'previousPeriodicControl'
    >
  > {
  dieselCardNumber?: DieselCardForm[]
  registrationDate?: Date | null | undefined
  soldDate?: Date | null | undefined
  nextPeriodicControl?: Date | null | undefined
  previousPeriodicControl?: Date | null | undefined
  department: string | null
}

export interface EditVehicleDetailsModalResolve {
  [key: string]: EditVehicleDetailsForm
  form: EditVehicleDetailsForm
}

export enum VehicleDetailFormField {
  Date = 'date',
  Boolean = 'boolean',
  Number = 'number',
  Country = 'country',
  Select = 'select',
  Radio = 'radio',
  Phone = 'phone',
}
